import { useGetAllMajorNames, useGetCitiesLight, useGetTimezonesList } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import { useDebounce } from '@hooks';
import { CreateUserV2 } from '@shared/apis/auth.apis';
import {
  CreateNewUserResponse,
  SelectOption,
  STUDENT_TIER_OPTIONS,
  StudentTier,
} from '@shared/clientTypes';
import { countryList, supportedLanguages } from '@shared/constants';
import { PhoneNumber } from '@shared/serverTypes';
import AgoraBigSelector from '@utils/AgoraBigSelector';
import AgoraInput from '@utils/AgoraInput';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';
import { UseMutateFunction } from 'react-query';
import { COLORS } from '../../../v3/global';
import './create-new-user.styles.scss';

type StudentDetails = {
  firstName: string;
  lastName: string;
  phoneNumber: PhoneNumber;
  parentName: string;
  parentEmail: string;
  parentPhoneNumber: PhoneNumber;
  city?: string;
  cityRef?: string;
  country: string;
  highschool: string;
  graduationYear: string;
  gpa: string;
  universities: string[];
  countriesToStudyIn: string[];
  major: string;
  spokenLanguages?: string[];
  timezone?: string;
  tier?: StudentTier;
  startContractDate?: string;
  endContractDate?: string;
  whatsappGroupLink?: string;
};

type Props = {
  email: string;
  role: 'STUDENT' | 'MENTOR' | 'ADMIN' | undefined;
  handleCurrentPage: (page: string) => void;
  studentDetails: StudentDetails;
  handleInputChangeStudent: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearCityStudent: () => void;
  handleClearCityRefStudent: () => void;
  setNewUserId: (string: string | undefined) => void;
  handleCountrySelectStudent: (value: any) => void;
  handleCityRefSelectStudent: (value: any) => void;
  handleCountriesToStudyInSelect: (value: any) => void;
  handleGraduationYearChange: (date: Moment | null) => void;
  handleUniversities: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSpokenLanguageSelectStudent: (value: any) => void;
  handleTimezoneSelectStudent: (value: any) => void;
  handleMajorSelectStudent: (value: string) => void;
  createUserV2: UseMutateFunction<CreateNewUserResponse, Error, CreateUserV2['payload']>;
  isCreatingUser: boolean;
  handleStudentTier: (value: StudentTier) => void;
  handleContractDateChange: (
    field: 'startContractDate' | 'endContractDate',
    date: Moment | null
  ) => void;
};

const CreateNewUserStudentStepTwo = ({
  email,
  role,
  handleCurrentPage,
  studentDetails,
  handleInputChangeStudent,
  handleClearCityStudent,
  handleClearCityRefStudent,
  setNewUserId,
  handleCountrySelectStudent,
  handleCityRefSelectStudent,
  handleCountriesToStudyInSelect,
  handleGraduationYearChange,
  handleUniversities,
  handleSpokenLanguageSelectStudent,
  handleTimezoneSelectStudent,
  handleMajorSelectStudent,
  createUserV2,
  isCreatingUser,
  handleStudentTier,
  handleContractDateChange,
}: Props) => {
  const [touched, setTouched] = useState<Record<keyof StudentDetails, boolean>>({
    firstName: false,
    lastName: false,
    phoneNumber: false,
    parentName: false,
    parentEmail: false,
    parentPhoneNumber: false,
    city: false,
    cityRef: false,
    country: false,
    highschool: false,
    graduationYear: false,
    gpa: false,
    universities: false,
    countriesToStudyIn: false,
    major: false,
    spokenLanguages: false,
    timezone: false,
    tier: false,
    startContractDate: false,
    endContractDate: false,
    whatsappGroupLink: false,
  });

  const [citySearch, setCitySearch] = useState('');
  const debouncedCitySearch = useDebounce(citySearch, 500);

  const { data: majorsList } = useGetAllMajorNames();
  const { data: timezones } = useGetTimezonesList();
  const { data: cities, isLoading: isLoadingCities } = useGetCitiesLight({
    search: debouncedCitySearch,
    limit: 20,
  });

  const getColor = (name: keyof typeof studentDetails) => {
    if (!touched[name]) {
      return COLORS.TEXT_SECONDARY;
    } else {
      if (name === 'universities' || name === 'countriesToStudyIn') {
        return studentDetails[name].length ? COLORS.PRIMARY : COLORS.ERROR;
      } else {
        return studentDetails[name] ? COLORS.PRIMARY : COLORS.ERROR;
      }
    }
  };

  const areDetailsFilled = !!(
    (studentDetails.city || studentDetails.cityRef) &&
    studentDetails.country &&
    studentDetails.graduationYear &&
    studentDetails.major
  );

  const getCityOptions = (): SelectOption[] => {
    return (
      cities?.map((city) => ({
        label: `${city.name}, ${city.county}`,
        value: city._id,
      })) || []
    );
  };

  const countrySelectHandler = (value?: string) => {
    handleCountrySelectStudent(value);

    if (value === 'Romania') {
      handleClearCityStudent();
    } else {
      handleClearCityRefStudent();
    }
  };

  return (
    <div className="mt-2">
      <div className="flex flex-col"></div>

      <div className="flex flex-col justify-between h-[60vh]">
        <div>
          <h3 className="modal-page-section-title mb-6 text-customGrey">Educational Background</h3>
          <div className="flex justify-between">
            <div className="w-[calc(50%-12px)] -mt-2">
              <AgoraBigSelector
                label="Country"
                isRequired
                labelColor={COLORS.TEXT_SECONDARY}
                onSelect={countrySelectHandler}
                onClear={() => countrySelectHandler('')}
                options={countryList.map((country) => ({
                  label: country,
                  value: country,
                }))}
                labelKey="label"
                valueKey="value"
                margin="0 8px 8px 0"
                height="38px"
                width="100%"
                borderRadius="8px"
                borderColor={COLORS.TEXT_SECONDARY}
              />
            </div>
            {studentDetails.country === 'Romania' ? (
              <div className="w-[calc(50%-12px)] -mt-2">
                <AgoraBigSelector
                  label="City"
                  isRequired
                  value={studentDetails.cityRef}
                  labelColor={COLORS.TEXT_SECONDARY}
                  onSelect={handleCityRefSelectStudent}
                  onClear={() => handleCityRefSelectStudent(undefined)}
                  onSearch={setCitySearch}
                  notFoundContent={
                    isLoadingCities ? <LoadingSpinner className="mx-auto" size="normal" /> : null
                  }
                  onBlur={() => setCitySearch('')}
                  options={getCityOptions()}
                  labelKey="label"
                  valueKey="value"
                  height="38px"
                  margin="0 0 8px 0"
                  width="100%"
                  borderRadius="8px"
                  borderColor={COLORS.TEXT_SECONDARY}
                />
              </div>
            ) : (
              <AgoraInput
                htmlFor="city"
                isRequired
                type="text"
                name="city"
                id="city"
                placeholder=""
                label="City"
                color={COLORS.TEXT_SECONDARY}
                labelColor={COLORS.TEXT_SECONDARY}
                borderColor={COLORS.TEXT_SECONDARY}
                labelMargin="0 0 0 8px"
                value={studentDetails?.city}
                margin="0 0 8px 0"
                onChange={handleInputChangeStudent}
                width="calc(50% - 12px)"
                // onBlur={handleBlur}
              />
            )}
          </div>
          <div className="flex justify-between">
            <div className="w-[calc(50%-12px)] -mt-1">
              <AgoraBigSelector
                label="Spoken Languages"
                labelColor={COLORS.TEXT_SECONDARY}
                onChange={(value) => handleSpokenLanguageSelectStudent(value)}
                onClear={() => handleSpokenLanguageSelectStudent([])}
                options={supportedLanguages.map((language) => ({
                  label: language,
                  value: language,
                }))}
                labelKey="label"
                valueKey="value"
                margin="0 0 8px 0"
                mode="multiple"
                height="38px"
                width="100%"
                borderRadius="8px"
                borderColor={COLORS.TEXT_SECONDARY}
              />
            </div>
            <div className="w-[calc(50%-12px)] -mt-2">
              <AgoraBigSelector
                label="Timezone"
                labelColor={COLORS.TEXT_SECONDARY}
                onSelect={(value) => handleTimezoneSelectStudent(value)}
                onClear={() => handleTimezoneSelectStudent('')}
                options={timezones?.map((timezone) => ({
                  label: timezone,
                  value: timezone,
                }))}
                labelKey="label"
                valueKey="value"
                margin="0 0 8px 0"
                height="38px"
                width="100%"
                borderRadius="8px"
                borderColor={COLORS.TEXT_SECONDARY}
              />
            </div>
          </div>
          <AgoraInput
            htmlFor="whatsappGroupLink"
            type="text"
            name="whatsappGroupLink"
            id="whatsappGroupLink"
            placeholder="https://chat.whatsapp.com/..."
            label="WhatsApp Group Link"
            color={COLORS.TEXT_SECONDARY}
            labelColor={COLORS.TEXT_SECONDARY}
            borderColor={COLORS.TEXT_SECONDARY}
            labelMargin="0 0 0 8px"
            value={studentDetails?.whatsappGroupLink}
            margin="0 0 8px 0"
            onChange={handleInputChangeStudent}
            width="100%"
          />
        </div>

        <div className="">
          <h3 className="modal-page-section-title mb-6 text-customGrey">Academic Path</h3>

          <div className="flex justify-between">
            <div className="w-[calc(50%-12px)]">
              <label
                style={{
                  color: getColor('graduationYear'),
                }}
                htmlFor="location"
                className="block text-sm font-medium leading-6 bg-transparent ml-2 text-customPrimary"
              >
                Graduation Year <span className="text-red-500">*</span>
              </label>

              <DatePicker
                picker="year"
                value={
                  studentDetails.graduationYear
                    ? moment(studentDetails.graduationYear, 'YYYY')
                    : null
                }
                onChange={handleGraduationYearChange}
                onClick={() =>
                  setTouched((prev) => ({
                    ...prev,
                    graduationYear: true,
                  }))
                }
                style={{
                  backgroundColor: 'transparent',
                  width: '100%',
                  height: '38px',
                  color: 'red',
                  borderColor: getColor('graduationYear'),
                }}
                clearIcon={null}
                className="custom-date-picker-secondary mt-2"
              />
            </div>

            <div className="w-[calc(50%-12px)]">
              <AgoraBigSelector
                label="Major (Target University Programs)"
                labelColor={getColor('major')}
                onSelect={(value) => {
                  handleMajorSelectStudent(value ?? '');
                  setTouched((prev) => ({
                    ...prev,
                    major: true,
                  }));
                }}
                onClear={() => handleMajorSelectStudent('')}
                options={majorsList?.map((major) => ({
                  label: major.name,
                  value: major._id ?? '',
                }))}
                labelKey="label"
                valueKey="value"
                value={studentDetails.major}
                margin="0 0 8px 0"
                height="38px"
                width="100%"
                borderRadius="8px"
                borderColor={getColor('major')}
                isRequired
              />
            </div>
          </div>
          <div
            onClick={() => {
              setTouched((prev) => ({
                ...prev,
                countriesToStudyIn: true,
              }));
            }}
          >
            <AgoraBigSelector
              label="Countries to study in"
              labelColor={getColor('countriesToStudyIn')}
              labelMargin="0 0 8px 0"
              onSelect={(value) => {
                handleCountriesToStudyInSelect([...studentDetails.countriesToStudyIn, value]);
              }}
              onDeselect={(value) => {
                const updatedCountries = studentDetails.countriesToStudyIn.filter(
                  (country) => country !== value
                );
                handleCountriesToStudyInSelect(updatedCountries);
              }}
              options={countryList.map((country) => ({
                label: country,
                value: country,
              }))}
              onClear={() => {
                handleCountriesToStudyInSelect([]);
              }}
              labelKey="label"
              valueKey="value"
              margin="0 0 8px 0"
              // height="38px"
              minHeight="38px"
              width="100%"
              borderRadius="8px"
              borderColor={getColor('countriesToStudyIn')}
              mode="multiple"
              value={studentDetails.countriesToStudyIn}
            />
          </div>

          <h3 className="modal-page-section-title mb-6 mt-8 text-customGrey">Contract</h3>

          <div className="flex justify-between gap-6">
            <div className="w-[calc(50%-12px)]">
              <AgoraBigSelector
                isRequired
                label="Tier"
                labelColor={COLORS.TEXT_SECONDARY}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    handleStudentTier(value as StudentTier);
                  } else if (typeof value === 'string' && value !== '') {
                    const numericTier = Number(value);
                    if (!Number.isNaN(numericTier)) {
                      handleStudentTier(numericTier as StudentTier);
                    }
                  }
                }}
                onClear={() => handleStudentTier(StudentTier.ClassicUniversityReadinessDegree)}
                options={STUDENT_TIER_OPTIONS}
                labelKey="label"
                valueKey="value"
                margin="0 0 8px 0"
                height="38px"
                width="100%"
                borderRadius="8px"
                borderColor={COLORS.TEXT_SECONDARY}
              />
            </div>
          </div>

          <div className="flex justify-between gap-6 mt-2">
            <div className="w-[calc(50%-12px)]">
              <label
                htmlFor="startContractDate"
                className="block text-sm font-medium leading-6 bg-transparent ml-2 text-customPrimary"
                style={{ color: COLORS.TEXT_SECONDARY }}
              >
                Start Contract Date
              </label>
              <DatePicker
                value={
                  studentDetails.startContractDate
                    ? moment(studentDetails.startContractDate)
                    : undefined
                }
                onChange={(value) => handleContractDateChange('startContractDate', value)}
                style={{
                  backgroundColor: 'transparent',
                  width: '100%',
                  height: '38px',
                  borderColor: COLORS.TEXT_SECONDARY,
                }}
                className="custom-date-picker-secondary mt-2"
              />
            </div>

            <div className="w-[calc(50%-12px)]">
              <label
                htmlFor="endContractDate"
                className="block text-sm font-medium leading-6 bg-transparent ml-2 text-customPrimary"
                style={{ color: COLORS.TEXT_SECONDARY }}
              >
                End Contract Date
              </label>
              <DatePicker
                value={
                  studentDetails.endContractDate
                    ? moment(studentDetails.endContractDate)
                    : undefined
                }
                onChange={(value) => handleContractDateChange('endContractDate', value)}
                style={{
                  backgroundColor: 'transparent',
                  width: '100%',
                  height: '38px',
                  borderColor: COLORS.TEXT_SECONDARY,
                }}
                className="custom-date-picker-secondary mt-2"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col ">
          <div className="flex justify-center items-center mb-6">
            <span className="create-new-user-modal-steps-text mr-4">Step 2 of 2</span>
            <div className="flex items-center justify-between w-8">
              <div className="create-new-user-modal-step-container">
                <div className="create-new-user-modal-step-circle"></div>
              </div>

              <div className="create-new-user-modal-step-container">
                <div className="create-new-user-modal-step-circle"></div>
              </div>
            </div>
          </div>
          <Button
            onClick={() => {
              if (areDetailsFilled) {
                createUserV2({
                  email: email,
                  userType: role ?? '',
                  permissions:
                    role === 'STUDENT'
                      ? { student: true }
                      : role === 'MENTOR'
                      ? { mentor: true }
                      : { admin: true },
                  studentData: {
                    firstName: studentDetails.firstName,
                    lastName: studentDetails.lastName,
                    phoneNumber: studentDetails.phoneNumber,
                    parentEmail: studentDetails.parentEmail,
                    parentFullName: studentDetails.parentName,
                    parentPhoneNumber: studentDetails.parentPhoneNumber,
                    spokenLanguages: studentDetails.spokenLanguages ?? [],
                    timezone: studentDetails.timezone ?? '',
                    major: studentDetails.major,
                    city: studentDetails.city || null,
                    cityRef: studentDetails.cityRef || null,
                    country: studentDetails.country,
                    highSchool: studentDetails.highschool,
                    graduationYear: Number(studentDetails.graduationYear),
                    gpa: isNaN(Number(studentDetails.gpa)) ? 0 : Number(studentDetails.gpa),
                    ecl: '',
                    standardisedTests: [],
                    universities: studentDetails.universities,
                    admissionExamPrep: [],
                    studySubjects: [],
                    subjectsStudentLikes: [],
                    subjectsStudentDoesntLike: [],
                    learningStyle: [],
                    countries: studentDetails.countriesToStudyIn,
                    interests: [],
                    tier: studentDetails.tier as StudentTier,
                    startContractDate: studentDetails.startContractDate || null,
                    endContractDate: studentDetails.endContractDate || null,
                    whatsappGroupLink: studentDetails.whatsappGroupLink || undefined,
                  },
                });
              }
            }}
            disabled={!areDetailsFilled}
            isLoading={isCreatingUser}
            buttonText={'Continue'}
            variant="primary"
            size="large"
          />
        </div>
      </div>
    </div>
  );
};

export default CreateNewUserStudentStepTwo;
